"use client";

import Link from "next/link";
import { Logo } from "./Logo";
import { useBoard } from "@/components/BoardProvider";
import { EXAM_BOARDS } from "@repo/exam-config";

const LINK_COLUMNS: { title: string; links: [string, string][] }[] = [
  {
    title: "Study",
    links: [
      ["Past papers", "/past-papers"],
      ["Universities", "/universities"],
      ["Textbook", "/textbook"],
      ["Features", "/features"],
      ["Pricing", "/pricing"],
      ["Scholarships", "/scholarship"],
    ],
  },
  {
    title: "The exam",
    links: [
      ["Key dates", "/exam-dates"],
      ["Format", "/exam-format"],
      ["Syllabus", "/syllabus"],
      ["Grade boundaries", "/grade-boundaries"],
    ],
  },
  {
    title: "Resources",
    links: [
      ["Articles", "/articles"],
      ["Mentors", "/mentors"],
      ["FAQs", "/faqs"],
    ],
  },
  {
    title: "Community",
    links: [
      ["Discord", "/discord"],
      ["WhatsApp", "/whatsapp"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy", "/privacy"],
      ["Terms", "/tos"],
    ],
  },
];

export function Footer() {
  const board = useBoard();
  const year = new Date().getFullYear();

  const sisterBoards = Object.values(EXAM_BOARDS).filter(
    (b) => b.id !== board.id && b.domains.length > 0,
  );

  // The retired-test comparison page only exists on boards that replaced one.
  const replaced = board.replacedTest;
  const columns = replaced
    ? LINK_COLUMNS.map((column) =>
        column.title === "The exam"
          ? {
              ...column,
              links: [
                ...column.links,
                [`${board.shortName} vs ${replaced.shortName}`, `/${replaced.slug}`] as [
                  string,
                  string,
                ],
              ],
            }
          : column,
      )
    : LINK_COLUMNS;

  return (
    <footer className="border-t border-border/20 bg-background/50 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 md:col-span-3 lg:col-span-1 flex flex-col items-start gap-4">
            <Logo />
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Helping sixth-formers conquer the {board.shortName} with adaptive
              practice and smart revision.
            </p>
          </div>
          {columns.map((column) => (
            <nav key={column.title} aria-label={column.title} className="flex flex-col gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">
                {column.title}
              </h3>
              {column.links
                .filter(([, href]) => !(href === "/mentors" && board.slug === "esat"))
                .map(([label, href]) => (
                  <Link
                    key={href}
                    href={href}
                    className="w-fit text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {label}
                  </Link>
                ))}
            </nav>
          ))}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-border/20 pt-6 text-xs text-muted-foreground">
          <span>
            &copy; {year} {board.domains[0] ?? `${board.slug}.fyi`}. All rights reserved.
          </span>
          {sisterBoards.length > 0 && (
            <span>
              Also from us:{" "}
              {sisterBoards.map((b, i) => (
                <span key={b.id}>
                  {i > 0 && ", "}
                  <a
                    href={`https://${b.domains[0]}`}
                    className="hover:text-foreground transition-colors"
                  >
                    {b.domains[0]}
                  </a>
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
